import { useState } from "react";
import { Alert, Button } from "flowbite-react";
import { useCurrentPositions } from "@repo/api";
import { useUniverseDetail } from "../../../../../hooks/api/useUniversesApi";
import { AccountSelector } from "../components/AccountSelector";
import AddRoboMarketsKeyModal from "../../../../../components/ui/AddRoboMarketsKeyModal";
import type { PageProps } from "../types";
import { useTranslation } from "@repo/core";

export const OverviewPage = ({
  strategy,
  selectedAccountId = "",
  onAccountSelect = () => { },
  isDropdownOpen = false,
  setIsDropdownOpen = () => { },
  nettingAccountsError,
  hasNoNettingAccounts = false,
  onCloseParentModal,
}: PageProps) => {
  const [showAddKeyModal, setShowAddKeyModal] = useState(false);
  const universe = useUniverseDetail(strategy?.poolName);
  const { t, tStrategy } = useTranslation();

  const currentPositionsQuery = useCurrentPositions(
    selectedAccountId || "",
    undefined,
    { enabled: !!selectedAccountId }
  );

  const currentPositions = currentPositionsQuery.data?.positions || [];

  // Check for RoboMarkets unauthorized error
  const isRoboMarketsUnauthorized =
    nettingAccountsError?.status === 500 &&
    nettingAccountsError?.roboMarketsStatus === 401;

  return (
    <div className="space-y-6">
      {/* RoboMarkets Unauthorized Error */}
      {isRoboMarketsUnauthorized && (
        <Alert color="failure">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <span className="font-medium">{tStrategy("authorizationError")}</span>
              <p className="mt-1">
                {tStrategy("unauthorizedRoboMarkets")}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <Button
              onClick={() => setShowAddKeyModal(true)}
              className="whitespace-nowrap"
            >
              {t("navbar.addRoboMarketsKey")}
            </Button>
          </div>
        </Alert>
      )}

      {/* Add RoboMarkets Key Modal */}
      <AddRoboMarketsKeyModal
        isOpen={showAddKeyModal}
        onClose={() => {
          setShowAddKeyModal(false);
          onCloseParentModal?.();
        }}
      />

      {/* Strategy Information */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-3">
          {tStrategy("strategyInformation")}
        </h4>
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              <span className="font-semibold">{tStrategy("builtOnIndicator")}</span>{" "}
              {Object.keys(strategy?.factor || {})[0]}
            </p>
            <p>
              {tStrategy("portfolioConsist")}{" "}
              {strategy?.backtest.numberOfStocks} {tStrategy("assetsRanked")}
            </p>
            <p>
              {tStrategy("strategyUsingLeverage")} {strategy?.backtest.leverage} {tStrategy("forInvestedCapital")}
            </p>
            <p>
              {strategy?.backtest.longsShare}% {tStrategy("capitalInvestedLong")}
            </p>
            <p>
              {strategy?.backtest.shortsShare}% {tStrategy("capitalInvestedShort")}
            </p>
            <p>0% {tStrategy("capitalLeftCash")}</p>
            <p>
              {tStrategy("universeRepresents")}{" "}
              {universe.data?.universe} index. {tStrategy("benchmarksCalculated")}
            </p>
          </div>
        </div>
      </div>

      {/* Account Selection */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-3">
          {tStrategy("selectTradingAccount")}
        </h4>
        <AccountSelector
          selectedAccountId={selectedAccountId}
          onAccountSelect={onAccountSelect}
          isDropdownOpen={isDropdownOpen}
          setIsDropdownOpen={setIsDropdownOpen}
        />
        {hasNoNettingAccounts && (
          <Alert color="warning" className="mt-3">
            <span className="font-medium">{tStrategy("noAccountsAvailable")}</span>
            <p className="mt-1">{tStrategy("nettingAccountRequired")}</p>
          </Alert>
        )}
      </div>

      {/* Current Positions */}
      {selectedAccountId && (
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-3">
            {tStrategy("currentPositions")}
          </h4>
          {currentPositionsQuery.isLoading ? (
            <div className="text-center py-4">
              <span className="text-gray-500">{tStrategy("loadingPositions")}</span>
            </div>
          ) : currentPositions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-900">
                      {tStrategy("ticker")}
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-900">
                      {tStrategy("side")}
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-900">
                      {tStrategy("volume")}
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-900">
                      {tStrategy("openPrice")}
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-900">
                      {tStrategy("profit")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentPositions.map((position, index) => (
                    <tr
                      key={`${position.ticker}-${index}`}
                      className="hover:bg-gray-50"
                    >
                      <td className="border border-gray-300 px-4 py-2 font-medium">
                        {position.ticker}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${position.side.toLowerCase() === "long"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                            }`}
                        >
                          {position.side}
                        </span>
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        {position.volume}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        ${position.open_price.toFixed(2)}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <span
                          className={`font-medium ${position.profit >= 0
                            ? "text-green-600"
                            : "text-red-600"
                            }`}
                        >
                          ${position.profit.toFixed(2)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-4">
              <span className="text-gray-500">{tStrategy("noPositionsFound")}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
